import { useContext, useEffect, useState } from "react";
import { FeatureFlagsContext } from "./context";

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error(
      "useFeatureFlags must be used within a FeatureFlagsProvider"
    );
  }
  return context;
};

export function useFeatureFlag(flagName: string, defaultValue = false) {
  const { isEnabled, isSettled } = useFeatureFlags();
  const [enabled, setEnabled] = useState(defaultValue);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSettled) return;
    const checkFlag = async () => {
      try {
        const value = await isEnabled(flagName);
        setEnabled(value);
      } catch (error) {
        console.error(`Error checking flag ${flagName}:`, error);
      } finally {
        setLoading(false);
      }
    };

    checkFlag();
  }, [flagName, isEnabled, isSettled]);

  return { enabled, loading };
}

export function useFeatureFlagValue<T>(flagName: string, defaultValue: T) {
  const { getObjectValue, isSettled } = useFeatureFlags();
  const [value, setValue] = useState<T>(defaultValue);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSettled) return;
    const fetchValue = async () => {
      try {
        const result = await getObjectValue(flagName, defaultValue);
        setValue(result);
      } catch (error) {
        console.error(`Error fetching flag value for ${flagName}:`, error);
      } finally {
        setLoading(false);
      }
    };

    fetchValue();
  }, [flagName, defaultValue, getObjectValue, isSettled]);

  return { value, loading };
}
